// Owner: WP05 (STORE01-04, MYTV01, AUTH05 client data). Public client-state API consumed by pages. Keep this surface stable.
// Module singleton + useSyncExternalStore (no provider). Scope comes from useSession(); the server is authoritative (STORE03).
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { Progress, StateOperation, UserState } from '@tuned/contracts';
import type { ApiRequestError } from '../app/api.ts';
import { useActiveConnection, useSession } from '../app/session.ts';
import type { CacheKind, CacheRecord } from './db.ts';
import { cacheable, getStore, toApiError, type CachePutOptions, type MutationHandle, type Snapshot, type StorageStatus, type Store } from './store.ts';

export type ListName = 'favorite' | 'watchlist' | 'follow' | 'hidden';
export type { CacheKind, CachePutOptions, CacheRecord, MutationHandle, StorageStatus };
export type UserStateApi = {
  state: UserState | null;
  loading: boolean;
  error: ApiRequestError | null;
  /** Showing the locally saved copy because the server could not be reached (or not yet). */
  stale: boolean;
  pendingCount: number;
  /** Optimistically apply, persist to the outbox, sync with operationId + baseRevision. delayMs defers sending (undo toasts). */
  mutate(op: StateOperation, opts?: { delayMs?: number }): MutationHandle;
  isInList(list: ListName, itemId: string): boolean;
  progressFor(itemId: string): Progress | null;
  refresh(): Promise<void>;
};
export type CachedResource<T> = {
  data: T | null;
  status: 'loading' | 'ready' | 'error';
  fromCache: boolean;
  stale: boolean;
  fetchedAt: number | null;
  error: ApiRequestError | null;
  refreshing: boolean;
  retry(): void;
};
export type CacheOptions<T> = { kind: CacheKind; maxAgeMs: number; sourceRevision?: (data: T) => string | null };

export { completionThresholdSec, isAutoCompleted } from './progress.ts';
export { indexCatalogItems, searchLocal, useLocalIndexReady, clearLocalIndex } from './localSearch.ts';
export type { Hit, IndexItem, QueryOptions } from './search-index.ts';

const useSnapshot = <T>(s: Store, select: (snap: Snapshot) => T): T => useSyncExternalStore(s.subscribe, () => select(s.getSnapshot()), () => select(s.getSnapshot()));

/** Points the store at the session's device/profile/connection. Every store hook calls it; call it yourself before the imperative cache API. */
export function useStoreScope(): void {
  const s = getStore();
  const { data, dataUpdatedAt } = useSession();
  const connectionId = useActiveConnection()?.id ?? null;
  const deviceId = data?.device.id;
  const profileId = data?.profile.id;
  const sharedMode = data?.device.sharedMode ?? false;
  const ids = data?.connections.map((c) => c.id).join('\n') ?? '';
  useEffect(() => {
    if (!deviceId || !profileId) return;
    void s.setScope({ deviceId, profileId, connectionId, sharedMode, connectionIds: ids ? ids.split('\n') : [], sessionUpdatedAt: dataUpdatedAt });
  }, [s, deviceId, profileId, connectionId, sharedMode, ids, dataUpdatedAt]);
}

export function useUserState(): UserStateApi {
  const s = getStore();
  useStoreScope();
  const snap = useSnapshot(s, (x) => x);
  return useMemo(() => {
    const st = snap.state;
    return {
      state: st, loading: snap.loading, error: snap.error, stale: snap.stale, pendingCount: snap.pendingCount,
      mutate: s.mutate, refresh: s.refresh,
      isInList: (list: ListName, itemId: string) => !!st?.lists.some((l) => l.list === list && l.itemId === itemId),
      progressFor: (itemId: string) => st?.progress.find((p) => p.itemId === itemId) ?? null,
    };
  }, [s, snap]);
}

export function useStorageStatus(): StorageStatus {
  useStoreScope();
  return useSnapshot(getStore(), (x) => x.storage);
}

/** Max 10, newest first; kept in the scoped database (memory in shared device mode), never localStorage. */
export function useRecentSearches(): { items: string[]; add(q: string): void; clear(): void } {
  const s = getStore();
  useStoreScope();
  const items = useSnapshot(s, (x) => x.recentSearches);
  return useMemo(() => ({ items, add: s.addRecent, clear: s.clearRecents }), [s, items]);
}

type ResourceState<T> = Omit<CachedResource<T>, 'retry'>;
const LOADING: ResourceState<never> = { data: null, status: 'loading', fromCache: false, stale: false, fetchedAt: null, error: null, refreshing: false };

/**
 * STORE02: show the cached copy at once, revalidate when older than maxAgeMs (or on retry), write back with
 * fetchedAt/staleAt/sourceRevision/schemaVersion. On failure keep the cached data (stale + error); no cache → 'error'.
 * Keys must not name credential, session, media or playback paths (throws in development).
 */
export function useCachedResource<T>(key: string | null, fetcher: (signal: AbortSignal) => Promise<T>, opts: CacheOptions<T>): CachedResource<T> {
  const s = getStore();
  useStoreScope();
  if (key) cacheable(key);
  const session = useSession();
  const scopeKey = useSnapshot(s, (x) => x.scopeKey);
  // Wait for the scope so the first read hits the right database; without a session (e.g. 401) fetch uncached.
  const scopeReady = scopeKey !== null || (!session.isPending && !session.data);
  const latest = useRef({ fetcher, opts });
  latest.current = { fetcher, opts };
  const force = useRef(false);
  const [nonce, setNonce] = useState(0);
  const [st, setSt] = useState<ResourceState<T> & { key: string | null }>({ ...LOADING, key: null });

  useEffect(() => (key ? s.onInvalidate((keys) => { if (keys.includes(key)) setNonce((n) => n + 1); }) : undefined), [s, key]);

  useEffect(() => {
    if (!key || !scopeReady) return;
    const ac = new AbortController();
    let live = true;
    const forced = force.current;
    force.current = false;
    void (async () => {
      const rec = await s.cacheGet<T>(key);
      if (!live) return;
      const now = Date.now();
      const fresh = !!rec && now < rec.staleAt && !forced;
      setSt(rec
        ? { key, data: rec.data, status: 'ready', fromCache: true, stale: now >= rec.staleAt, fetchedAt: rec.fetchedAt, error: null, refreshing: !fresh }
        : { ...LOADING, key, refreshing: true });
      if (fresh) return;
      try {
        const data = await latest.current.fetcher(ac.signal);
        if (!live) return;
        const { kind, maxAgeMs, sourceRevision } = latest.current.opts;
        void s.cachePut(key, data, { kind, maxAgeMs, sourceRevision: sourceRevision?.(data) ?? null });
        setSt({ key, data, status: 'ready', fromCache: false, stale: false, fetchedAt: Date.now(), error: null, refreshing: false });
      } catch (e) {
        if (!live) return;
        const error = toApiError(e);
        setSt((p) => (p.key === key && p.data !== null ? { ...p, stale: true, error, refreshing: false } : { ...LOADING, key, status: 'error', error }));
      }
    })();
    return () => {
      live = false;
      ac.abort();
    };
  }, [s, key, scopeKey, scopeReady, nonce]);

  const retry = useMemo(() => () => { force.current = true; setNonce((n) => n + 1); }, []);
  return useMemo(() => {
    const { key: forKey, ...rest } = st;
    return { ...(key && forKey === key ? rest : LOADING), retry };
  }, [key, st, retry]);
}

/** Imperative cache access for features that manage their own fetching (e.g. guide windows with kind 'guide'). */
export const cacheGet = <T>(key: string): Promise<CacheRecord<T> | undefined> => getStore().cacheGet<T>(key);
export const cachePut = <T>(key: string, data: T, opts: CachePutOptions): Promise<void> => getStore().cachePut(key, data, opts);
export const cacheDelete = (key: string): Promise<void> => getStore().cacheDelete(key);
/** Settings → "Clear downloaded metadata": cached catalog, guide, detail and search records plus the local search index. Keeps user state and the outbox. */
export const clearCachedMetadata = (): Promise<void> => getStore().clearCachedMetadata();

/** AUTH05 sign out of this device: deletes every 'tuned:' database and Cache Storage entry, clears memory, tells other tabs. */
export const signOutClientData = (): Promise<void> => getStore().signOut();
/** Cross-tab message (BroadcastChannel 'tuned'); not delivered to the sending tab. No-op when BroadcastChannel is missing. */
export const broadcast = (type: string, payload?: unknown): void => getStore().broadcast(type, payload);
export const subscribeBroadcast = (type: string, cb: (payload: unknown) => void): (() => void) => getStore().subscribeBroadcast(type, cb);
/** Called after this tab signs out and when another tab broadcasts 'logout'. */
export const onLogout = (cb: () => void): (() => void) => getStore().onLogout(cb);
